import { BellRing } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  currentBrowserSubscription,
  pushSubscriptionsApi,
  pushSupported,
  subscribeCurrentBrowser,
} from "@/lib/api/push-subscriptions"

interface PushNotificationPromptProps {
  shouldPrompt?: boolean
  vapidPublicKey?: string | null
}

export default function PushNotificationPrompt({
  shouldPrompt = false,
  vapidPublicKey,
}: PushNotificationPromptProps) {
  const [open, setOpen] = React.useState(false)
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!shouldPrompt || !vapidPublicKey || !pushSupported()) return
    if (Notification.permission === "denied") {
      void pushSubscriptionsApi.dismissPrompt()
      return
    }

    const timer = window.setTimeout(() => setOpen(true), 900)

    return () => window.clearTimeout(timer)
  }, [shouldPrompt, vapidPublicKey])

  const dismiss = async () => {
    setIsProcessing(true)
    setError(null)

    try {
      await pushSubscriptionsApi.dismissPrompt()
      setOpen(false)
    } catch (dismissError) {
      setError(
        dismissError instanceof Error
          ? dismissError.message
          : "Could not save your preference.",
      )
    } finally {
      setIsProcessing(false)
    }
  }

  const enable = async () => {
    if (!vapidPublicKey) return

    setIsProcessing(true)
    setError(null)

    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        await pushSubscriptionsApi.dismissPrompt()
        setOpen(false)
        return
      }

      const subscription =
        (await currentBrowserSubscription()) ??
        (await subscribeCurrentBrowser(vapidPublicKey))
      await pushSubscriptionsApi.create(subscription)
      setOpen(false)
    } catch (enableError) {
      setError(
        enableError instanceof Error
          ? enableError.message
          : "Could not enable notifications.",
      )
    } finally {
      setIsProcessing(false)
    }
  }

  if (!shouldPrompt || !vapidPublicKey) return null

  return (
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-md bg-gray-900 text-white">
            <BellRing className="size-5" />
          </div>
          <DialogTitle>Do you want to turn on notifications?</DialogTitle>
          <DialogDescription>
            Get updates for new posts, replies, and account notifications on this
            device.
          </DialogDescription>
        </DialogHeader>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={dismiss}
            disabled={isProcessing}
          >
            No thanks
          </Button>
          <Button type="button" onClick={enable} disabled={isProcessing}>
            Enable notifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
